/* eslint-disable jsx-a11y/alt-text */

import {
  Currency,
  Formatter,
  useCMSBlogs,
  useCMSFeatureds,
  useCMSTestimonials,
  useMobile,
  usePricing,
} from "@with-nx/hooks-n-helpers";
import { useServerRegion } from "@with-nx/region";
import { DesignedButton, DesignedCard, Rule } from "@with-nx/simple-ui/atoms";
import { ReusableSection } from "@with-nx/simple-ui/atoms";
import { BlueUnderline, VisibleContainer } from "@with-nx/simple-ui/molecules";
import {
  AutoScroller,
  GradientHolder,
  GridBlog,
  GridStepScroller,
  ProductLandingHero,
  PromotionSection,
  ReusableDoubleGradientCard,
  ReusablePricingBox,
  StagePlayerInAction,
  TestimonialQuotes,
} from "@with-nx/simple-ui/organisms";
import {
  CatalogLayout,
  ReusableContainerPartners,
} from "@with-nx/simple-ui/organisms";
import { Col, Row } from "antd";
import { useRouter } from "next/router";
import { useState } from "react";
import { Box } from "simple-effing-primitive-layout";

import { ReusableScenicProjectionsFeatures } from "../components/ReusableScenicProjectionsFeatures";
import ReusableSearchSection from "../components/w/ReusableSearchSection";

const StaticShowVariantData = [
  {
    logo: "/assets/variants/show-variant-1.webp",
    url: "/shows/disneys-frozen/frozen-jr",
    color: "#00ABE3",
    name: "Disney's Frozen Jr.",
  },
  {
    logo: "/assets/variants/show-variant-2.webp",
    url: "/shows/annie/annie",
    color: "#000000",
    name: "Annie",
  },
  {
    logo: "/assets/variants/show-variant-3.webp",
    url: "/shows/disneys-beauty-and-the-beast/beauty-and-the-beast",
    color: "#000000",
    name: "Disney's Beauty and the Beast",
  },
  {
    logo: "/assets/variants/show-variant-4.webp",
    url: "/shows/anastasia/anastasia",
    color: "#222036",
    name: "Anastasia",
  },
  {
    logo: "/assets/variants/show-variant-5.webp",
    url: "/shows/wizard-of-oz/wizard-of-oz",
    color: "#01DA63",
    name: "Wizard of Oz",
  },
  {
    logo: "/assets/variants/show-variant-6.webp",
    url: "/shrek-the-musical/shrek-musical",
    color: "#000000",
    name: "Shrek the Musical",
  },
  {
    logo: "/assets/variants/show-variant-7.webp",
    url: "/mamma-mia/mamma-mia",
    color: "#8CD1F0",
    name: "Mamma Mia!",
  },
  {
    logo: "/assets/variants/show-variant-8.webp",
    url: "/shows/seussical/seussical",
    color: "#000000",
    name: "Seussical",
  },
  {
    logo: "/assets/variants/show-variant-9.webp",
    url: "/shows/the-addams-family/addams-family",
    color: "#FF1205",
    name: "The Addams Family",
  },
  {
    logo: "/assets/variants/show-variant-10.webp",
    url: "/shows/clue/clue",
    color: "#FFFFFF",
    name: "Clue",
  },
  {
    logo: "/assets/variants/show-variant-11.webp",
    url: "/shows/disneys-the-lion-king/lion-king-jr",
    color: "#FFFFFF",
    name: "Disney's The Lion King Jr.",
  },
  {
    logo: "/assets/variants/show-variant-12.webp",
    url: "/disneys-moana/moana-jr",
    color: "#FFFFFF",
    name: "Disney's Moana Jr.",
  },
  {
    logo: "/assets/variants/show-variant-13.webp",
    url: "/shows/big-fish/big-fish",
    color: "#FFFAAA",
    name: "Big Fish",
  },
];

const ScenicProjections = ({ resolvedUrl }) => {
  const router = useRouter();

  const blogs = useCMSBlogs({
    exists: {
      scenicProjectionTags: true,
    },
  });

  const [all] = useState(false);

  const region = useServerRegion();
  const featureds = useCMSFeatureds({});
  const _featureds = featureds.featureds.filter((i) => {
    return (
      i?.pageTags?.includes("Scenic Projections Landing Page") &&
      i?.regionTags?.includes(region?.id?.toString() || "")
    );
  });

  const mobile = useMobile();
  const { testimonials } = useCMSTestimonials({
    per: 5,
  });

  const juniorPriceTable = usePricing({
    type: "SCENIC_PROJECTION_PACKAGE",
    variation: "JUNIOR",
    region: region?.id,
  });

  const regularPriceTable = usePricing({
    type: "SCENIC_PROJECTION_PACKAGE",
    variation: "REGULAR",
    region: region?.id,
  });

  const juniorPrice = Currency.formatInteger(
    Number(juniorPriceTable?.price) || 0,
    region?.currency?.code || "USD"
  );

  const regularPrice = Currency.formatInteger(
    Number(regularPriceTable?.price) || 0,
    region?.currency?.code || "USD"
  );

  const standardLicensePrice = `${juniorPrice} - ${regularPrice}`;
  const proLicensePrice = Currency.formatInteger(
    Number(regularPriceTable?.priceProLicense) || 0,
    region?.currency?.code || "USD"
  );

  return (
    <CatalogLayout
      footerNoAngle
      title="Broadway Media - Scenic Projections"
      description="Scenic Projections packages are perfectly curated show collections with the script cue lines and descriptions."
      resolvedUrl={resolvedUrl}
    >
      <ProductLandingHero
        title={
          <>
            <Rule parse="!ll d:block mb:12 ta:center">
              Make Your Stage Come Alive with
            </Rule>
            <Rule parse="!dm d:block mb:12 ta:center" weight="700" tag="span">
              Scenic Projections
            </Rule>
          </>
        }
        description={
          <>
            Scenic Projections packages are perfectly curated show collections{" "}
            <br />
            with the script cue lines and descriptions.
          </>
        }
        primaryButtonLabel="Find Your Show"
        primaryButtonHref="#shows"
        secondaryButtonLabel="Schedule Consultation"
        secondaryButtonPress={() => {
          router.push("/contact/calendar");
        }}
      >
        <AutoScroller duration={30}>
          {StaticShowVariantData?.map((item, i) => (
            <Box
              parse={`w:${mobile ? 200 : 400} mr:18 ml:18`}
              key={i}
              style={{ minWidth: mobile ? 200 : 400 }}
            >
              <DesignedCard
                key={i}
                size="16x9"
                pop={false}
                inside={{
                  image: item?.logo,
                  color: item?.color,
                  large: item?.name,
                }}
                href={item?.url}
              />
            </Box>
          ))}
        </AutoScroller>
      </ProductLandingHero>

      <ReusableSection>
        <div className="container">
          <Box parse="mh:450">
            {!!testimonials?.length && (
              <TestimonialQuotes testimonials={testimonials} />
            )}
          </Box>
        </div>
      </ReusableSection>

      <ReusableContainerPartners
        title="Exclusive Authorized Providers of Digital Scenery"
        titleTag="h3"
        slanted={false}
        filter={{
          partnerTypeTag: "Licensor",
        }}
        align="center"
        colored
      />

      <ReusableScenicProjectionsFeatures />

      <ReusableSection bottom={0}>
        <div className="container">
          <Box parse="d:flex mb:36">
            <Rule parse="d:flex" tag="h5">
              <Box parse="p:relative">
                <Rule
                  parse="!hl"
                  css="--blue-gradient-text"
                  bottom={0}
                  style={{ marginBottom: 0 }}
                  tag="span"
                >
                  Featured&nbsp;
                  <BlueUnderline />
                </Rule>
              </Box>

              <Rule parse="!hl mb:0" tag="span">
                Scenic Projections
              </Rule>
            </Rule>
          </Box>

          <GridStepScroller
            xs={24}
            sm={24}
            md={8}
            items={[
              ..._featureds.map((i, ii) => {
                if (!all && ii >= 3) {
                  return null;
                }

                if (all && ii >= 9) {
                  return null;
                }

                return (
                  <GradientHolder
                    src={Formatter.image(i.image, true)}
                    key={i._id}
                    render={(colors) => (
                      <ReusableDoubleGradientCard
                        key={i._id}
                        gradients={
                          colors.ready
                            ? [colors.hex, colors.hex]
                            : ["transparent", "transparent"]
                        }
                        title={i.name}
                        holder={false}
                        press={() => {
                          router.push(i.url);
                        }}
                        properties={{ height: 384 }}
                      >
                        <Box css="p:absolute t:0 l:0 w:100% h:100% d:flex a:center j:center">
                          {colors.ready ? (
                            <img
                              loading="lazy"
                              width="176"
                              height="176"
                              src={colors.src}
                              alt={i.name || "Broadway Media"}
                            />
                          ) : undefined}
                        </Box>
                      </ReusableDoubleGradientCard>
                    )}
                  />
                );
              }),
            ]}
          />
          {all ? undefined : (
            <Box css="d:flex j:flex-end" parse="mt:24">
              <DesignedButton
                size="small"
                theme="tetriary"
                label="View All Shows"
                href="#shows"
              />
            </Box>
          )}
        </div>
      </ReusableSection>

      <PromotionSection page="Scenic Projections Landing Page" />

      <StagePlayerInAction
        title="Pre-Cued Show Packages"
        subtitle="Script-Accurate Digital Backdrops Delivered in StagePlayer"
        isMobile={mobile}
      />

      <div id="shows" />
      <VisibleContainer>
        <ReusableSection>
          <div className="container">
            <Rule parse="!hl d:block mb:24">
              Discover the Scenic Projections Library
            </Rule>
          </div>
          <ReusableSearchSection productCategory="scenic-projections" />
        </ReusableSection>
      </VisibleContainer>
      <ReusableSection>
        <div className="container">
          <Rule parse="!hl d:block mb:36 ta:center">
            Scenic Projections License Types
          </Rule>
          <Row gutter={[36, 18]}>
            <Col xs={24} sm={24} md={12}>
              <ReusablePricingBox
                title="Standard License"
                disclaimer={`${juniorPrice}  for Youth versions and ${regularPrice} for Mainstage`}
                popular={true}
                label="4-week license"
                price={standardLicensePrice}
                features={[
                  "Animated Videos",
                  "Every Scene and Setting",
                  "Fully Programmed Cues",
                  "Integrated Cue Sheet",
                  "Rehearsal Content",
                  "Delivered through StagePlayer",
                ]}
                onLearnMore={() => {
                  router.push("/contact/calendar");
                }}
                mode="top-left-yellow"
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <ReusablePricingBox
                featuresLabel="Capabilities:"
                title="Pro License"
                description="Customizable scenery for advanced projection programmed in the software of your choice"
                popular={false}
                label="4-week license"
                price={proLicensePrice}
                features={[
                  "Media Server Mapping",
                  "QLab Integration",
                  "Mapping Textures",
                ]}
                onLearnMore={() => {
                  router.push("/contact/calendar");
                }}
                mode="top-left-grey"
              />
            </Col>
          </Row>
        </div>
      </ReusableSection>
      <ReusableSection>
        <div className="container">
          <Row gutter={[36, 18]} align="middle">
            <Col xs={24} sm={24} md={12}>
              <Rule parse="!hl d:block mb:12" css="--blue-gradient-text">
                The power of Scenic Projections
              </Rule>
              <Rule parse="!ht d:block mb:24 c:?font1">
                Bring Your Stage to Life with Unparalleled Quality and Ease
              </Rule>
              <Rule parse="!pl d:block mb:12 c:?font2">
                The trusted choice for thousands of theaters across the globe.
                Scenic Projections offer unparalleled quality and ease of use.
                Plug in and perform with pre-cued digital backdrop packages for
                popular plays and musicals, designed to enhance your production
                design process.
              </Rule>
              <Box parse="mb:36">
                <DesignedButton
                  label="Request Information"
                  theme="tetriary"
                  properties={{ right: 10 }}
                  href={`/contact/start?mode=learn-more&referrer=${router.asPath}`}
                />
              </Box>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <DesignedCard
                image="/assets/section/scenic-projections-landing-page-1.webp"
                size="16x9"
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <DesignedCard
                image="/assets/section/scenic-projections-landing-page-2.webp"
                size="16x9"
              />
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Rule parse="!ht d:block mb:12 c:?font2">
                Elevate your stage and transport your audience to extraordinary
                worlds. Experience the future of theatrical design today
              </Rule>
              <Rule parse="!pl d:block mb:36 c:?font3">
                With every scene and setting crafted to match your script,
                Scenic Projections deliver a seamless experience using our
                intuitive StagePlayer software. Say goodbye to traditional
                backdrops and hello to a new era of projection technology that
                saves you money, reduces blackouts, and maximizes stage space!
              </Rule>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <Rule parse="!ht d:block mb:12 c:?font2">
                Endorsed by{" "}
                <a
                  href="/partners/mti"
                  rel="noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  Music Theatre International
                </a>
                ,{" "}
                <a
                  href="/partners/broadway-licensing"
                  rel="noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  Broadway Licensing
                </a>{" "}
                and{" "}
                <a
                  href="/partners/trw"
                  rel="noreferrer"
                  style={{ textDecoration: "underline" }}
                >
                  Theatrical Rights Worldwide
                </a>
                , our animated digital projection packages feature
                script-accurate scenes and settings.
              </Rule>
              <Rule parse="!pl d:block mb:12 c:?font3">
                Our pre-cued digital backdrop packages for popular plays and
                musicals simplify production design, empowering directors and
                educators to create immersive theatre within the real-world
                limits of time and budget.
              </Rule>
            </Col>
            <Col xs={24} sm={24} md={12}>
              <DesignedCard
                image="/assets/section/scenic-projections-landing-page-3.webp"
                size="16x9"
              />
            </Col>
          </Row>
        </div>
      </ReusableSection>
      {blogs.blogs.length > 0 ? (
        <ReusableSection background="transparent">
          <div className="container">
            <Rule parse="!hl d:block mb:36">
              Learn More about Scenic Projections
            </Rule>
          </div>
          <GridStepScroller
            xs={24}
            sm={24}
            md={12}
            lg={8}
            xl={8}
            items={blogs.blogs.map((blog, b) => {
              if (b > 2) {
                return null;
              }

              return (
                <GridBlog
                  key={blog._id}
                  date={blog.date}
                  description={blog.description}
                  image={Formatter.compressed(blog.coverImage, 256, true)}
                  title={blog.title}
                  url={`/blog/${blog.slug}`}
                  transparent={false}
                  properties={{
                    height: "100%",
                  }}
                />
              );
            })}
          />
          <div className="container">
            <Box css="d:flex j:flex-end" parse="mt:24">
              <DesignedButton
                size="small"
                theme="tetriary"
                label="See More Stories"
                href="/blog"
              />
            </Box>
          </div>
        </ReusableSection>
      ) : undefined}
    </CatalogLayout>
  );
};

export default ScenicProjections;

export const getServerSideProps = async ({ resolvedUrl }) => {
  return {
    props: {
      resolvedUrl,
    },
  };
};
